import './index.css'
import { useState } from 'react'
import { supabase } from '../../lib/supabase'
import { getToastErrorMessage, useToast } from '../Toast'

function IconGoogle() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  )
}

function translateError(msg: string): string {
  if (msg.includes('email rate limit'))        return 'Muitos cadastros recentes. Aguarde 1 hora e tente novamente.'
  if (msg.includes('User already registered')) return 'Este e-mail já está cadastrado.'
  if (msg.includes('Invalid login credentials')) return 'E-mail ou senha incorretos.'
  if (msg.includes('Email not confirmed'))     return 'Confirme seu e-mail antes de entrar.'
  if (msg.includes('Password should be'))     return 'A senha deve ter pelo menos 6 caracteres.'
  return msg
}

interface AuthScreenProps {
  onBack?: () => void
}

export default function AuthScreen({ onBack }: AuthScreenProps) {
  const toast = useToast()
  const [mode, setMode]           = useState<'login' | 'signup'>('login')
  const [email, setEmail]         = useState('')
  const [password, setPassword]   = useState('')
  const [consent, setConsent]     = useState(false)
  const [error, setError]         = useState<string | null>(null)
  const [loading, setLoading]     = useState(false)
  const [signupDone, setSignupDone] = useState(false)

  async function handleGoogleSignIn() {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: window.location.href },
      })
      if (error) throw error
    } catch (err) {
      toast.error(getToastErrorMessage(err, 'Erro ao iniciar login com Google.'))
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      if (mode === 'login') {
        const { error } = await supabase.auth.signInWithPassword({ email, password })
        if (error) throw error
      } else {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: window.location.href },
        })
        if (error) throw error
        // Household, member e categorias são criados automaticamente pelo trigger
        // on_auth_user_created no banco de dados.
        if (!data.session) {
          // Confirmação de e-mail exigida — mostrar aviso
          setSignupDone(true)
          toast.success('Conta criada. Confira seu e-mail.')
          return
        }
        toast.success('Conta criada.')
        // Com confirmação desativada, onAuthStateChange em App.tsx cuida do resto
      }
    } catch (err) {
      const message = translateError(getToastErrorMessage(err, 'Erro de autenticação.'))
      setError(message)
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{
      minHeight: '100dvh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--sand-50)',
      padding: '1rem',
    }}>
      <div style={{
        background: 'var(--sand-0, #fff)',
        borderRadius: '1rem',
        padding: '2rem',
        width: '100%',
        maxWidth: '22rem',
        boxShadow: '0 4px 24px rgba(0,0,0,.08)',
      }}>
        {onBack && (
          <button
            onClick={onBack}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.375rem',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.8125rem',
              color: 'var(--ink-400, #6b7280)',
              padding: '0',
              marginBottom: '1.25rem',
              fontFamily: 'var(--font-ui)',
            }}
          >
            ← Voltar ao início
          </button>
        )}

        <h1 style={{
          fontFamily: 'var(--font-display)',
          fontSize: '1.5rem',
          marginBottom: '0.25rem',
          color: 'var(--ink-900)',
        }}>
          BagCash
        </h1>

        {signupDone ? (
          <>
            <p style={{ fontSize: '0.875rem', color: 'var(--ink-500)', marginBottom: '1.5rem' }}>
              Conta criada! Verifique seu e-mail e clique no link de confirmação para entrar.
            </p>
            <button
              className="bc-btn bc-btn--primary"
              style={{ width: '100%' }}
              onClick={() => { setSignupDone(false); setMode('login') }}
            >
              Ir para o login
            </button>
          </>
        ) : (
          <>
            <p style={{ fontSize: '0.875rem', color: 'var(--ink-500)', marginBottom: '1.5rem' }}>
              {mode === 'login' ? 'Acesse sua conta' : 'Crie sua conta'}
            </p>

            <button
              type="button"
              onClick={handleGoogleSignIn}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.625rem',
                width: '100%',
                padding: '0.625rem 1rem',
                background: 'white',
                border: '1px solid var(--sand-300, #d1c9bb)',
                borderRadius: '0.625rem',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: 600,
                fontFamily: 'var(--font-ui)',
                color: 'var(--ink-800, #1a2e1f)',
                marginBottom: '1rem',
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--sand-50, #faf7f2)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'white')}
            >
              <IconGoogle />
              Continuar com Google
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <div style={{ flex: 1, height: '1px', background: 'var(--sand-200, #e5dfd4)' }} />
              <span style={{ fontSize: '0.75rem', color: 'var(--ink-400, #6b7280)', fontFamily: 'var(--font-ui)' }}>ou</span>
              <div style={{ flex: 1, height: '1px', background: 'var(--sand-200, #e5dfd4)' }} />
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--ink-600)', marginBottom: '0.25rem' }}>
                  E-mail
                </label>
                <input
                  className="bc-input"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  style={{ width: '100%', boxSizing: 'border-box' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--ink-600)', marginBottom: '0.25rem' }}>
                  Senha
                </label>
                <input
                  className="bc-input"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  minLength={6}
                  style={{ width: '100%', boxSizing: 'border-box' }}
                />
              </div>

              {mode === 'signup' && (
                <label className="bc-auth-consent">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={e => setConsent(e.target.checked)}
                    required
                  />
                  <span>
                    Li e aceito os{' '}
                    <a href="/termos" target="_blank" rel="noopener noreferrer">Termos de Uso</a>
                    {' '}e a{' '}
                    <a href="/privacidade" target="_blank" rel="noopener noreferrer">Política de Privacidade</a>
                  </span>
                </label>
              )}

              {error && (
                <p style={{ fontSize: '0.75rem', color: 'var(--expense-500, #e25c4a)', margin: 0 }}>
                  {error}
                </p>
              )}

              <button
                className="bc-btn bc-btn--primary"
                type="submit"
                disabled={loading || (mode === 'signup' && !consent)}
                style={{ marginTop: '0.25rem', width: '100%' }}
              >
                {loading ? 'Aguarde…' : mode === 'login' ? 'Entrar' : 'Criar conta'}
              </button>
            </form>

            <button
              onClick={() => { setMode(m => m === 'login' ? 'signup' : 'login'); setError(null); setConsent(false) }}
              style={{
                marginTop: '1rem',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '0.8125rem',
                color: 'var(--ink-500)',
                width: '100%',
                textAlign: 'center',
              }}
            >
              {mode === 'login' ? 'Não tem conta? Cadastre-se' : 'Já tem conta? Entrar'}
            </button>
          </>
        )}
      </div>
    </div>
  )
}
